import { useState, useCallback, useRef } from "react";
import {
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  Input,
  Textarea,
  Button,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  IconButton,
  Grid,
  Text,
} from "@chakra-ui/react";
import { Icon } from "@chakra-ui/react";
import { Upload, X } from "lucide-react";
import { useThemeColors } from "@/hooks/useThemeColors";
import { apiClient } from "@/lib/api/client";
import { useToastNotification } from "@/lib/utils/useToastNotification";
import { sendSoaEmailSchema } from "@/lib/validations/auth";

const ALLOWED_ATTACHMENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "image/gif",
  "image/webp",
] as const;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const MAX_ATTACHMENT_FILES = 10;
const ACCEPT_ATTR = "image/jpeg,image/png,image/gif,image/webp,.jpeg,.jpg,.png,.gif,.webp";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseEmails(input: string): string[] {
  return input
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e.length > 0);
}

function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email);
}

export interface EmailSoaFormProps {
  /** Shipping line name passed from parent (read-only). */
  shippingLineName: string;
  /** SOA ID (required for send-email API). */
  soaId: number;
  onSuccess?: () => void;
  onCancel: () => void;
}

export function EmailSoaForm({
  shippingLineName,
  soaId,
  onCancel,
  onSuccess,
}: EmailSoaFormProps) {
  const colors = useThemeColors();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState("");
  const [ccInput, setCcInput] = useState("");
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [ccError, setCcError] = useState<string | null>(null);
  const { showError, showSuccess } = useToastNotification();

  const validateAttachmentFile = useCallback((file: File): string | null => {
    const type = file.type?.toLowerCase() ?? "";
    if (!ALLOWED_ATTACHMENT_TYPES.includes(type as (typeof ALLOWED_ATTACHMENT_TYPES)[number])) {
      return `"${file.name}": must be JPEG, PNG, JPG, GIF, or WebP.`;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `"${file.name}": must be 5MB or less.`;
    }
    return null;
  }, []);

  const handleAttachmentChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAttachmentError(null);
      const input = e.target;
      const fileList = input.files;
      if (!fileList || fileList.length === 0) return;

      const next: File[] = [];
      const errors: string[] = [];
      for (let i = 0; i < fileList.length; i++) {
        if (next.length >= MAX_ATTACHMENT_FILES) {
          errors.push(`Maximum ${MAX_ATTACHMENT_FILES} files allowed.`);
          break;
        }
        const file = fileList[i];
        const err = validateAttachmentFile(file);
        if (err) errors.push(err);
        else next.push(file);
      }
      if (errors.length > 0) {
        setAttachmentError(errors.slice(0, 3).join(" "));
      }
      setAttachmentFiles((prev) => {
        const combined = [...prev, ...next].slice(0, MAX_ATTACHMENT_FILES);
        return combined;
      });
      input.value = "";
    },
    [validateAttachmentFile]
  );

  const removeAttachment = useCallback((index: number) => {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
    setAttachmentError(null);
  }, []);

  const addCcEmail = useCallback(() => {
    setCcError(null);
    const parsed = parseEmails(ccInput);
    const invalid = parsed.filter((e) => !isValidEmail(e));
    if (invalid.length > 0) {
      setCcError(`Invalid email(s): ${invalid.join(", ")}`);
      return;
    }
    const newEmails = parsed.filter((e) => !ccEmails.includes(e));
    setCcEmails((prev) => [...prev, ...newEmails]);
    setCcInput("");
  }, [ccInput, ccEmails]);

  const removeCcEmail = useCallback((index: number) => {
    setCcEmails((prev) => prev.filter((_, i) => i !== index));
    setCcError(null);
  }, []);

  const handleCcKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === "," || e.key === ";") {
        e.preventDefault();
        addCcEmail();
      }
    },
    [addCcEmail]
  );

  const handleSubmit = useCallback(async () => {
    setEmailError(null);
    setCcError(null);

    const payload = {
      email: email.trim(),
      cc: ccEmails,
      subject: subject.trim() || undefined,
      body: body.trim() || undefined,
    };

    const parsed = sendSoaEmailSchema.safeParse(payload);
    if (!parsed.success) {
      const issues = parsed.error.flatten().fieldErrors;
      setEmailError(issues.email?.[0] ?? null);
      setCcError(issues.cc?.[0] ?? null);
      return;
    }

    const validData = parsed.data;
    if (typeof soaId !== "number" || !Number.isFinite(soaId)) {
      showError(new Error("SOA ID is required to send email."));
      return;
    }

    setIsSubmitting(true);
    try {
      const hasAttachments = attachmentFiles.length > 0;

      if (hasAttachments) {
        setIsUploadingAttachments(true);
        try {
          const formData = new FormData();
          attachmentFiles.forEach((file) => {
            formData.append("images[]", file, file.name);
          });
          await apiClient.request({
            method: "POST",
            url: "/soa-and-billing/attachments",
            data: formData,
            timeout: 60000,
            headers: {
              "Content-Type": undefined,
            },
          });
        } catch (uploadError: unknown) {
          showError(uploadError as Error);
          return;
        } finally {
          setIsUploadingAttachments(false);
        }
      }

      await apiClient.post(`/soa/${soaId}/send-email`, validData, {
        params: { include_attachments: hasAttachments },
      });

      showSuccess("Success", "SOA email sent successfully.");
      onSuccess?.();
    } catch (error: unknown) {
      showError(error as Error);
    } finally {
      setIsSubmitting(false);
      setIsUploadingAttachments(false);
    }
  }, [email, ccEmails, subject, body, attachmentFiles, soaId, onSuccess, showError, showSuccess]);

  return (
    <VStack spacing={4} align="stretch">
      <Grid
        templateColumns={{ base: "1fr", md: "140px 1fr" }}
        gap={4}
        alignItems="center"
      >
        <FormLabel fontWeight="medium" color={colors.text} mb={0}>
          Shipping Line:
        </FormLabel>
        <Text
          color={colors.text}
          bg={colors.sectionBg}
          px={3}
          py={2}
          borderRadius="md"
          borderWidth="1px"
          borderColor={colors.border}
        >
          {shippingLineName || "-"}
        </Text>
      </Grid>

      <FormControl isInvalid={!!emailError} isRequired>
        <FormLabel fontWeight="medium" color={colors.text}>
          Email Address:
        </FormLabel>
        <Input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailError(null);
          }}
          placeholder="client@example.com"
          bg="white"
          borderColor={colors.border}
          color={colors.text}
          _placeholder={{ color: colors.textSecondary }}
        />
        {emailError && (
          <FormHelperText color="red.500" mt={1}>
            {emailError}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl isInvalid={!!ccError}>
        <FormLabel fontWeight="medium" color={colors.text}>
          CC:
        </FormLabel>
        <Input
          type="text"
          value={ccInput}
          onChange={(e) => {
            setCcInput(e.target.value);
            setCcError(null);
          }}
          onKeyDown={handleCcKeyDown}
          onBlur={addCcEmail}
          placeholder="Add CC emails (input comma or press Enter in keyboard to add)"
          bg="white"
          borderColor={colors.border}
          color={colors.text}
          _placeholder={{ color: colors.textSecondary }}
        />
        {ccEmails.length > 0 && (
          <Wrap spacing={2} mt={2}>
            {ccEmails.map((addr, index) => (
              <WrapItem key={`${addr}-${index}`}>
                <Tag
                  size="md"
                  colorScheme="gray"
                  borderRadius="full"
                  gap={1}
                  variant="subtle"
                >
                  <TagLabel maxW="180px" noOfLines={1} title={addr}>
                    {addr}
                  </TagLabel>
                  <IconButton
                    aria-label={`Remove ${addr}`}
                    size="xs"
                    variant="ghost"
                    icon={<Icon as={X} boxSize={3} />}
                    onClick={() => removeCcEmail(index)}
                    minW={6}
                    minH={6}
                  />
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        )}
        {ccError && (
          <FormHelperText color="red.500" mt={1}>
            {ccError}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl>
        <FormLabel fontWeight="medium" color={colors.text}>
          Subject:
        </FormLabel>
        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject"
          bg="white"
          borderColor={colors.border}
          color={colors.text}
          _placeholder={{ color: colors.textSecondary }}
        />
      </FormControl>

      <FormControl>
        <FormLabel fontWeight="medium" color={colors.text}>
          Body:
        </FormLabel>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Body"
          rows={6}
          bg="white"
          borderColor={colors.border}
          color={colors.text}
          _placeholder={{ color: colors.textSecondary }}
        />
      </FormControl>

      {/* Attachments – file attachments */}
      <FormControl isInvalid={!!attachmentError}>
        <FormLabel fontWeight="medium" color={colors.text}>
          Attachments
        </FormLabel>
        <FormHelperText color={colors.textSecondary} mt={0}>
          JPEG, PNG, JPG, GIF, or WebP. Max 5MB each. Max {MAX_ATTACHMENT_FILES} files.
        </FormHelperText>
        <VStack align="stretch" spacing={3} mt={2}>
          <HStack spacing={2} flexWrap="wrap">
            <Button
              size="sm"
              leftIcon={<Icon as={Upload} boxSize={4} />}
              onClick={() => fileInputRef.current?.click()}
              isDisabled={attachmentFiles.length >= MAX_ATTACHMENT_FILES}
              variant="outline"
              borderColor={colors.border}
              color={colors.text}
            >
              Choose images
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_ATTR}
              multiple
              onChange={handleAttachmentChange}
              style={{ display: "none" }}
              aria-hidden
            />
            {attachmentFiles.length > 0 && (
              <Text fontSize="sm" color={colors.textSecondary}>
                {attachmentFiles.length} file(s) selected
              </Text>
            )}
          </HStack>
          <FormErrorMessage>{attachmentError ?? ""}</FormErrorMessage>
          {attachmentFiles.length > 0 && (
            <Wrap spacing={2}>
              {attachmentFiles.map((file, index) => (
                <WrapItem key={`${file.name}-${index}`}>
                  <Tag size="md" colorScheme="blue" borderRadius="full" gap={1}>
                    <TagLabel maxW="120px" noOfLines={1} title={file.name}>
                      {file.name}
                    </TagLabel>
                    <IconButton
                      aria-label={`Remove ${file.name}`}
                      size="xs"
                      variant="ghost"
                      icon={<Icon as={X} boxSize={3} />}
                      onClick={() => removeAttachment(index)}
                      minW={6}
                      minH={6}
                    />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          )}
        </VStack>
      </FormControl>

      <HStack spacing={4} justify="flex-end" pt={2} w="full">
        <Button
          variant="outline"
          onClick={onCancel}
          borderColor={colors.border}
          color={colors.text}
        >
          Cancel
        </Button>
        <Button
          colorScheme="blue"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          loadingText={isUploadingAttachments ? "Uploading..." : "Sending..."}
        >
          Send to Client
        </Button>
      </HStack>
    </VStack>
  );
}
